/**
 * Snapshot API service.
 * Provides operations for snapshots.
 *
 * API Endpoints:
 * - GET /snapshots - List all snapshots
 * - GET /snapshots/:id - Get snapshot by ID
 *
 * @module services/snapshotService
 */

import { httpClient } from './httpClient';
import type { SnapshotResponse } from '@/types';

const ENDPOINT = '/snapshots';

/**
 * Snapshot Service
 * Encapsulates all snapshot API operations
 */
export const snapshotService = {
  /**
   * Get total count of snapshots
   * @returns Promise<number> - Total count
   */
  async getCount(): Promise<number> {
    console.log('[SnapshotService] Fetching snapshots count...');
    try {
      const response = await httpClient.get<number>(`${ENDPOINT}/count`);
      console.log('[SnapshotService] Total snapshots:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SnapshotService] Failed to fetch snapshots count:', error);
      throw error;
    }
  },

  /**
   * List snapshots with pagination
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<SnapshotResponse[]> - Array of snapshots
   */
  async list(
    page: number = 1,
    pageSize: number = 25
  ): Promise<import('@/types').SnapshotResponse[]> {
    console.log('[SnapshotService] Fetching snapshots - Page:', page, 'PageSize:', pageSize);
    try {
      const response = await httpClient.get<import('@/types').SnapshotResponse[]>(
        `${ENDPOINT}?page=${page}&pageSize=${pageSize}`
      );
      console.log('[SnapshotService] Fetched', response.data.length, 'snapshots');
      return response.data;
    } catch (error) {
      console.error('[SnapshotService] Failed to fetch snapshots:', error);
      throw error;
    }
  },

  /**
   * List snapshots for a specific sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @returns Promise<SnapshotResponse[]> - Array of snapshots in the group
   */
  async listForGroup(sandboxGroupId: string): Promise<SnapshotResponse[]> {
    const encoded = encodeURIComponent(sandboxGroupId);
    try {
      const response = await httpClient.get<SnapshotResponse[]>(
        `/sandboxGroups/${encoded}/snapshots?page=1&pageSize=100`
      );
      return response.data;
    } catch (error) {
      console.error('[SnapshotService] Failed to fetch group snapshots:', sandboxGroupId, error);
      throw error;
    }
  },

  /**
   * Get a snapshot by ID
   * @param id - The ID of the snapshot
   * @returns Promise<SnapshotResponse> - The snapshot
   */
  async getById(id: string): Promise<SnapshotResponse> {
    console.log('[SnapshotService] Fetching snapshot:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<SnapshotResponse>(`${ENDPOINT}/${encodedId}`);
      console.log('[SnapshotService] Fetched snapshot:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SnapshotService] Failed to fetch snapshot:', id, error);
      throw error;
    }
  },

  /**
   * Delete a snapshot by ID
   * @param id - The ID of the snapshot to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    console.log('[SnapshotService] Deleting snapshot:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[SnapshotService] Deleted snapshot:', id);
    } catch (error) {
      console.error('[SnapshotService] Failed to delete snapshot:', id, error);
      throw error;
    }
  },
};
